import React from 'react'
import Image from 'next/image'
import type { RTImageNode, RTNode } from '@/types/prismic'
import { PrismicRichText, PrismicRichTextProps, JSXMapSerializer } from '@prismicio/react'

// Define the props for our LazyImage component
interface LazyImageProps {
  src: string
  alt: string
  width: number
  height: number
}

// LazyImage component using Next.js Image with lazy loading
const LazyImage: React.FC<LazyImageProps> = ({ src, alt, width, height }) => {
  const isSvg = /\.svg(\?|$)/i.test(src)

  return (
    <Image
      src={isSvg ? src.split('?')[0] : src}
      alt={alt}
      width={width}
      height={height}
      loading="lazy"
      unoptimized={isSvg}
      {...(isSvg
        ? {}
        : {
            placeholder: 'blur' as const,
            blurDataURL:
              'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F9PQAI/wMKoKu7RAAAAABJRU5ErkJggg==',
          })}
    />
  )
}

// OptimizedPrismicRichText component
const OptimizedPrismicRichText: React.FC<PrismicRichTextProps> = ({ field, components: userComponents, ...rest }) => {
  const defaultComponents: JSXMapSerializer = {
    image: ({ node }: { node: RTNode }) => {
      if (node.type === 'image' && 'url' in node) {
        const imageNode = node as RTImageNode
        return (
          <LazyImage
            src={imageNode.url}
            alt={imageNode.alt || ''}
            width={imageNode.dimensions?.width || 0}
            height={imageNode.dimensions?.height || 0}
          />
        )
      }
      return null
    },
  }

  const mergedComponents: JSXMapSerializer = {
    ...defaultComponents,
    ...userComponents,
  }

  return <PrismicRichText field={field} components={mergedComponents} {...rest} />
}

export default OptimizedPrismicRichText
